import React, { useState } from 'react';
import PropTypes from 'prop-types';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import InputGroup from 'Components/ComponentLibrary/Molecules/InputGroup';
import { FDPaper, FDSimpleAccordion, FDStack } from '@facet-wealth/design';
import { partition } from 'Helpers/UtilsWithoutLodash';
import { FDInputRow } from 'Packages/FacetDesign/Components/Experimental/fdInputRow';
import { dataAttrs } from 'Helpers/Testing';
import getFieldsWithGroups from './getFieldListWithGroups';

// Once upon a time, bad defaults were invented. We don't want those bad defaults, but if we change them globally, that affects all forms and could be bad.
// So we'll just define better defaults here and only impact newer forms
const CustomProps = { showReadOnlyOptions: true };

const Fields = ({ fields, horizontal, fullWidth }) => {
  const content = fields.map(field => {
    if (Array.isArray(field)) {
      return (
        <InputGroup key={field.map(f => f.id()).join()}>
          {field.map(f => (<MetaField CustomProps={fullWidth ? { ...CustomProps, fullWidth } : CustomProps} notInLayout attr={f.attr('name')} key={f.id()} />))}
        </InputGroup>
      );
    }

    return (<MetaField CustomProps={fullWidth ? { ...CustomProps, fullWidth } : CustomProps} notInLayout attr={field.attr('name')} key={field.id()} />);
  });

  if (!horizontal) {
    return content;
  }
  return <FDInputRow>{content}</FDInputRow>;
};
Fields.propTypes = {
  horizontal: PropTypes.bool.isRequired,
  fields: PropTypes.array.isRequired,
  fullWidth: PropTypes.bool,
};
Fields.defaultProps = {
  fullWidth: undefined,
};

const shouldAlwaysShow = (field) => (Array.isArray(field) ? field.some(f => shouldAlwaysShow(f)) : field.attr('required'));

/**
 * MetaLayoutRemainingFields returns the meta fields that aren't being rendered in layout.
 * This allows the consumer of this component to decide the layout of the extra fields.
 */
export default function MetaLayoutRemainingFields({
  noAccordion, horizontal, accordionTitle, expanded, fullWidth,
}) {
  const {
    remainingFields,
    resource,
  } = useMetaLayoutContext();
  const [_expanded, setExpanded] = useState(expanded);

  const [alwaysShownFields, accordionFields] = partition(
    getFieldsWithGroups(resource, remainingFields),
    noAccordion
      ? () => true // Never put anything in an accordion, put everything under alwaysShownFields
      : shouldAlwaysShow, // Put things in accordions if they shouldn't always be shown
  );

  // If there would be no fields here and this is in a column with a gap, we need to return null if there are no fields to avoid returning an empty div that causes a weird-looking gap
  if (accordionFields.length === 0 && alwaysShownFields.length === 0) {
    return null;
  }

  return (
    <FDStack direction={'column'} alignItems={'stretch'} spacing={2}>
      <Fields fields={alwaysShownFields} horizontal={horizontal} fullWidth={fullWidth} />
      {accordionFields.length > 0 && (
        <FDPaper color="subtle" appearance="flat" {...dataAttrs('remaining-fields-accordion', resource)}>
          <FDSimpleAccordion title={accordionTitle} expanded={_expanded} onChange={setExpanded}>
            <FDStack direction={'column'} alignItems={'stretch'} spacing={2}>
              <Fields fields={accordionFields} horizontal={horizontal} fullWidth={fullWidth} />
            </FDStack>
          </FDSimpleAccordion>
        </FDPaper>
      )}
    </FDStack>
  );
}
MetaLayoutRemainingFields.defaultProps = {
  noAccordion: false,
  horizontal: false,
  accordionTitle: 'More...',
  expanded: false,
  fullWidth: undefined,
};
MetaLayoutRemainingFields.propTypes = {
  noAccordion: PropTypes.bool,
  horizontal: PropTypes.bool,
  accordionTitle: PropTypes.string,
  expanded: PropTypes.bool,
  fullWidth: PropTypes.bool,
};
